import { useRecoilState } from 'recoil';
import { RegularButton, useMediaQuery } from 'fronton-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { getCookie } from 'utils/cookie.helper';
import { Snackbar } from 'pages/common/components/Snackbar';
import { TProjectConfigurationGeneralItem } from 'pages/common/types';
import { useUpdateDataContext } from 'pages/saved-project-configuration/hooks/use-update-data-context';
import { DataContext, RouteContext } from 'entries/common/widgets/saved-project-configuration/context';
import { savedConfigurationCalculationState } from 'pages/saved-project-configuration/store/calculation';
import { LayingType, LayingTypeLabel } from 'pages/common/components/LayingTypeField/const';
import { useMeasurement } from 'pages/common/hooks/use-measurement';
import { useEditConfigurationRequest } from 'pages/saved-project-configuration/hooks/use-edit-configuration-request';
import { getDimensionsFormHeading } from 'pages/common/helpers/get-dimensions-form-heading';
import { getDimensionsFormSubheading } from 'pages/common/helpers/get-dimensions-form-subheading';
import { REGION_ID_COOKIE } from '../../../common/consts/cookies';
import { measurementServiceIds } from '../../../common/consts/services';
import { routes } from '../../consts/routes';
import { MeasurementBanner } from '../../components/MeasurementBanner';
import { ProjectConfigurationParams } from '../../components/ProjectConfigurationParams';
import { getUpdatedConfigurationParameters } from '../../helpers/get-updated-configuration-params';
import { getDefaultParams } from '../../helpers/get-default-params';
import { getActiveTab } from '../../helpers/get-active-tab';
import { getAreaUser } from '../../helpers/get-area-user';
import { TInputs } from './types';
import {
  FormStyled,
  Container,
  StyledHeading,
  ContentContainer,
  FooterContainer,
  FooterWrapper,
  MeasurementBannerWrapper,
} from './styles';

const FORM_NAME = 'dimensions-form';

export const Dimensions = () => {
  const { data } = useContext(DataContext);
  const { setRoute } = useContext(RouteContext);
  const [calculationFormState, setCalculationState] = useRecoilState(savedConfigurationCalculationState);
  const [activeTabIndex, setActiveTabIndex] = useState(getActiveTab(data?.projectConfigurationParameters));
  const [isFormLoading, setIsFormLoading] = useState(false);
  const { editConfigurationRequest } = useEditConfigurationRequest();
  const { updateDataContext } = useUpdateDataContext();
  const methods = useForm<TInputs>();
  const isFormDirty = methods.formState.isDirty;

  const regionId = getCookie(REGION_ID_COOKIE);
  const defaultValues = useMemo(() => getDefaultParams(data?.projectConfigurationParameters), [
    data?.projectConfigurationParameters,
  ]);
  const isMobile = useMediaQuery({
    maxWidth: 767,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const { isAvailable } = useMeasurement({
    serviceId: measurementServiceIds.LAMINATE,
    regionId,
    api: data?.api,
  });

  const { dirtyFields } = methods.formState;

  const onSubmit: SubmitHandler<TInputs> = async (inputsData: TInputs) => {
    setIsFormLoading(true);

    if (!isFormDirty || !data?.api) {
      setRoute(routes.calculation);
      return;
    }

    const body = {
      regionId,
      inputParameters: {
        areaUser: getAreaUser(inputsData),
      },
      surface: { FLOOR: { typeInstallation: LayingTypeLabel[inputsData.layingType as LayingType] } },
    };
    const mainProductCode = (calculationFormState.mainProduct as TProjectConfigurationGeneralItem).code;
    const updatedConfigurationParams = getUpdatedConfigurationParameters(inputsData, dirtyFields);

    try {
      const calculateParams = await data.api.post(`/calculate/${mainProductCode}`, body);

      updateDataContext({ calculateParams, updatedConfigurationParams });
      await editConfigurationRequest({ calculateParams, updatedConfigurationParams, regionId });
      updateRecoilState();
      setRoute(routes.calculation);
    } catch {
      setRoute(routes.error);
    } finally {
      setIsFormLoading(false);
    }
  };

  const updateRecoilState = () => {
    setCalculationState(prevState => ({
      ...prevState,
      isInit: false,
    }));
  };

  return (
    <FormProvider {...methods}>
      <Container data-testid="project-dimensions">
        <StyledHeading data-testid="dimensions-title">{getDimensionsFormHeading(isMobile)}</StyledHeading>
        {getDimensionsFormSubheading(isMobile, true)}
        <ContentContainer>
          <FormStyled id={FORM_NAME} onSubmit={methods.handleSubmit(onSubmit)}>
            <ProjectConfigurationParams
              setActiveTabIndex={setActiveTabIndex}
              activeTabIndex={activeTabIndex}
            />
          </FormStyled>
          {isAvailable && (
            <MeasurementBannerWrapper>
              <MeasurementBanner />
            </MeasurementBannerWrapper>
          )}
        </ContentContainer>
        <FooterContainer>
          <FooterWrapper>
            <RegularButton
              loading={isFormLoading}
              variant="primary"
              label="Рассчитать"
              type="submit"
              form={FORM_NAME}>
              Рассчитать
            </RegularButton>
          </FooterWrapper>
        </FooterContainer>
        <Snackbar horizontalPosition="end" verticalPosition="bottom" />
      </Container>
    </FormProvider>
  );
};
